import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput
} from '@mui/material';
import {
  IErrorMessage,
  useErrorMessage
} from '@shared/hooks/use-error-message';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import GenreAddDialog from '@modules/entertainment/components/genre-add-dialog/genre-add-dialog';
import AppDialog from '@shared/components/app-dialog/app-dialog';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog';
import AppAutocompleteWithActions from '@shared/components/app-autocomplete-with-actions/app-autocomplete-with-actions';
import { GenresEnum } from '@modules/entertainment/models/entertainment.models';
import InfoTooltip from '@shared/components/info-tooltip/info-tooltip';
import useMusicStore from '../../store/music.store';
import { CrudActionEnum } from '@models/app-autocomplete.model.ts';
import { MusicDialogFormProps } from '@modules/entertainment/models/music.models';
import { validateNotOnlySpaces } from '@shared/utils/trim-form-values-utils';
import { FileTypeEnum } from '@models/media-library.model.ts';
import MediaLibraryComponent from '@components/media-library-component/media-library-component.tsx';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import { LanguageEnum } from '@models/language.model.ts';

const MusicDialogForm: React.FC<MusicDialogFormProps> = ({
  selectedItem,
  onSave,
  genreList
}) => {
  const {
    register,
    reset,
    handleSubmit,
    control,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      title: '',
      authorName: '',
      genres: []
    }
  });

  const getErrorMessage = useErrorMessage();

  const { t } = useTranslation();
  const { isLoading, isGenreLoading, createGenre, editGenre, deleteGenre } =
    useMusicStore();
  const { resetSelectedMedia } = useMediaLibraryStore();

  const [showDeleteDialog, setShowDeleteDialog] =
    React.useState<boolean>(false);
  const [showAddNewGenreDialog, setShowAddNewGenreDialog] =
    React.useState(false);
  const [genresLists, setGenresLists] = React.useState<any[]>([]);
  const [selectedGenreValue, setSelectedGenreValue] = useState<any>(null);
  const [selectedMedia, setSelectedMedia] = React.useState<Record<string, any>>(
    {}
  );

  const errorMessages: IErrorMessage = {
    title: {
      required: 'validation_title_required',
      notOnlySpaces: 'validation_notOnly_spaces_required'
    },
    authorName: {
      required: 'validation_author_name_required',
      notOnlySpaces: 'validation_notOnly_spaces_required'
    },
    genres: {
      required: 'validation_genres_required'
    },
    image: {
      required: 'validation_image_required'
    },
    audio: {
      required: 'validation_audio_required'
    }
  };

  const onSubmit = (data: any) => {
    const fullData: any = {
      title: data.title,
      authorName: data.authorName,
      image: selectedMedia.image?.path || null,
      file: selectedMedia.audio?.path || null,
      genres: genresLists.map(({ id }) => id)
    };

    onSave(fullData);
  };

  const handleCloseGenreDialog = () => {
    resetSelectedMedia('genreImage');
    setShowAddNewGenreDialog(false);
    setShowDeleteDialog(false);
    setSelectedGenreValue({ id: '', name: '' });
  };

  const handleSaveGenre = async (value: any) => {
    if (selectedGenreValue?.id) {
      editGenre({
        data: value,
        id: selectedGenreValue?.id,
        handleClose: handleCloseGenreDialog
      });
    } else {
      createGenre({
        data: value,
        handleClose: handleCloseGenreDialog
      });
    }
  };

  const handleDeleteConfirm = async () => {
    deleteGenre({
      id: selectedGenreValue?.id,
      handleClose: () => setShowDeleteDialog(false)
    });
  };

  const itemActionHandler = (value: any, type: CrudActionEnum) => {
    if (type === CrudActionEnum.Add) {
      setSelectedGenreValue({ name: value });
      setShowAddNewGenreDialog(true);
    } else if (type === CrudActionEnum.Edit) {
      setSelectedGenreValue(value);
      setShowAddNewGenreDialog(true);
    } else if (type === CrudActionEnum.Delete) {
      setSelectedGenreValue(value);
      setShowDeleteDialog(true);
    }
  };

  useEffect(() => {
    if (selectedItem) {
      const { title, authorName } = selectedItem;

      const genreObjects =
        selectedItem.genres?.map((genre: any) => ({
          ...genre,
          id: genre._id,
          name: genre.name[LanguageEnum.English] || Object.values(genre.name)[0]
        })) || [];

      reset({
        title: title || '',
        authorName: authorName || '',
        genres: genreObjects
      });

      setGenresLists(genreObjects);
    }
  }, [selectedItem, reset]);

  return (
    <div>
      <form className="flex flex-col gap-1">
        <div className="flex gap-4">
          <div className="media-wrapper mb-3">
            <div className="flex mb-1.5">
              <span className="reuqired-symbole">*</span>
              {t('label_music_file')}
              <InfoTooltip text="tooltip_info_music_file" />
            </div>
            <MediaLibraryComponent
              mediaKey={'audio'}
              title={'upload_audio_content_text'}
              allowedTypes={[FileTypeEnum.Audio]}
              path={selectedItem?.file}
              onSelect={(media) =>
                setSelectedMedia((prev) => ({ ...prev, audio: media }))
              }
            />
          </div>
          <div className="media-wrapper">
            <div className="flex mb-1.5">
              <span className="reuqired-symbole">*</span>
              {t('label_music_cover_image')}
              <InfoTooltip text="tooltip_info_music_cover_image" />
            </div>
            <MediaLibraryComponent
              mediaKey={'image'}
              allowedTypes={[FileTypeEnum.Image]}
              path={selectedItem?.image}
              onSelect={(media) =>
                setSelectedMedia((prev) => ({ ...prev, image: media }))
              }
            />
          </div>
        </div>

        <FormControl error={!!errors.title}>
          <FormLabel className="flex">
            <span className="reuqired-symbole">*</span>
            {t('label_music_title')}
            <InfoTooltip text="tooltip_info_music_title" />
          </FormLabel>
          <OutlinedInput
            {...register('title', {
              required: true,
              validate: {
                notOnlySpaces: validateNotOnlySpaces
              }
            })}
            error={!!errors.title}
            placeholder={t('placeholder_label_music_title')}
          />
          <FormHelperText>
            {errors?.title &&
              getErrorMessage({
                errors,
                fieldName: 'title',
                errorMessages
              })}
          </FormHelperText>
        </FormControl>

        <FormControl error={!!errors.authorName}>
          <FormLabel className="flex">
            <span className="reuqired-symbole">*</span>
            {t('label_author_name')}
            <InfoTooltip text="tooltip_info_music_author_name" />
          </FormLabel>
          <OutlinedInput
            {...register('authorName', {
              required: true,
              validate: {
                notOnlySpaces: validateNotOnlySpaces
              }
            })}
            error={!!errors.authorName}
            placeholder={t('placeholder_label_author_name')}
          />
          <FormHelperText>
            {errors?.authorName &&
              getErrorMessage({
                errors,
                fieldName: 'authorName',
                errorMessages
              })}
          </FormHelperText>
        </FormControl>

        <AppAutocompleteWithActions
          name="genres"
          control={control}
          label={
            <span className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_genres')}
              <InfoTooltip text="tooltip_info_music_genres" />
            </span>
          }
          placeholder={t('placeholder_label_music_genres')}
          options={genreList || []}
          selectedList={genresLists || []}
          onChange={setGenresLists}
          itemActionHandler={itemActionHandler}
          validationProps={{
            errors,
            errorMessages,
            getErrorMessage,
            validationRules: { required: true }
          }}
        />

        <div className="flex justify-end items-center gap-6 pt-4 pb-1 sticky bottom-0 bg-white">
          <Button
            disabled={!isValid || !selectedMedia.image || !selectedMedia.audio}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            loading={isLoading()}
          >
            {t('save')}
          </Button>
        </div>
      </form>

      <AppDialog
        open={showAddNewGenreDialog}
        title={t('entertainment_add_genre')}
        content={
          <GenreAddDialog
            loading={isGenreLoading()}
            selectedValue={selectedGenreValue}
            type={GenresEnum.Music}
            handleSave={handleSaveGenre}
            showLanguageTabs={false}
          />
        }
        onClose={handleCloseGenreDialog}
      />

      <div className="w-full h-full">
        <DeleteDialog
          onCancel={handleCloseGenreDialog}
          onDelete={handleDeleteConfirm}
          open={showDeleteDialog}
          title={t('delete_popup_title')}
          isLoading={isGenreLoading()}
        />
      </div>
    </div>
  );
};

export default MusicDialogForm;
